import { DateFormats } from 'helpers/enums/DateTypes';
import { clsx, isMobile, isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import { ReportStatus, ReportType } from 'models/relatory.model';
import moment from 'moment';
import {
  DownloadIcon,
  IconButton,
  PercentLoaderIcon,
  RefreshIcon,
  ThreeDotsLoader,
} from 'plataforma-fundacao-componentes';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { ReactComponent as ClockIcon } from './clock.svg';
import { ReactComponent as DoneIcon } from './done.svg';
import { ReactComponent as FileEmptyIcon } from './file.svg';
import { ReactComponent as FileDoneIcon } from './file2.svg';
import styles from './relatoryCard.module.scss';
import { ReactComponent as WarningIcon } from './warning.svg';

interface RelatoryCardProps {
  title?: string;
  buttonClick: () => Promise<any>;
  refetchClick?: () => Promise<any>;
  status?: ReportStatus;
  disabled?: boolean;
  nextDate?: string;
  ldap?: string;
  className?: string;
  date?: string;
  w100?: boolean;
  h100?: boolean;
  expanded?: boolean;
  type?: ReportType;
  reportData?: {
    lastLdap: string;
    lastDate: string;
    nextDate: string;
    version: number;
    status: ReportStatus;
  } | null;
}

const RelatoryCard: React.FC<RelatoryCardProps> = ({
  title = 'Relatório de Presenças',
  className,
  buttonClick,
  refetchClick,
  ldap = '',
  nextDate,
  disabled,
  date,
  status,
  w100 = true,
  h100 = false,
  expanded = false,
  reportData,
  type,
}) => {
  const [toggle, setToggle] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expand, setExpand] = useState<boolean>(expanded);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleRefetchClick = useCallback(async () => {
    if (refetchClick) {
      setIsLoading(true);
      setToggle(false);
      try {
        await refetchClick();
      } finally {
        setIsLoading(false);
      }
    }
  }, [refetchClick]);

  const handleButtonClick = useCallback(async () => {
    if (buttonClick && !isLoading) {
      setIsLoading(true);
      setToggle(false);
      try {
        await buttonClick();
      } finally {
        setIsLoading(false);
      }
    }
  }, [buttonClick, isLoading]);

  const renderIcon = useMemo(() => {
    switch (status) {
      case ReportStatus.READY:
        return <DoneIcon />;
      case ReportStatus.GENERATING:
        return <ClockIcon />;
      case ReportStatus.NOT_AVAILABLE:
        return <ClockIcon />;
      case ReportStatus.ERROR:
        return <WarningIcon />;
      default:
        return <WarningIcon />;
    }
  }, [status]);

  const renderFileIcon = useMemo(() => {
    return status === ReportStatus.READY ? <FileDoneIcon /> : <FileEmptyIcon />;
  }, [status]);

  const renderContent = useMemo(() => {
    if (status === ReportStatus.READY) {
      return (
        <div>
          <p>Baixar relatório</p>
          <span style={{ width: '100%', display: 'inline-flex' }}>
            Gerado {moment(date).format('DD/MM/YYYY')}
            <br />
            às {moment(date).format('HH:mm')}
            {ldap && (
              <Fragment>
                <br />
                por: {ldap}
              </Fragment>
            )}
          </span>
        </div>
      );
    } else {
      return (
        <div>
          <span>
            {type === ReportType.EVENT_SURVEY && reportData?.status === 'ERROR'
              ? 'Erro ao gerar o relatório, entre em contato com o Suporte'
              : type === ReportType.EVENT_SURVEY &&
                isNullOrUndefined(reportData)
              ? 'Relatório será disponibilizado no dia posterior a finalização do evento, tente novamente mais tarde.'
              : !disabled
              ? 'Gerar relatório'
              : 'Relatório indisponível '}
          </span>
        </div>
      );
    }
  }, [date, disabled, ldap, reportData, status, type]);

  useEffect(() => {
    if (isMobile()) {
      setExpand(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile()]);

  const dateIsAfter = useMemo(() => {
    return moment().isAfter(String(moment(nextDate)));
  }, [nextDate]);

  return (
    <div
      className={clsx(
        styles.root,
        w100 && styles.w100,
        h100 && styles.h100,
        !disabled &&
          refetchClick &&
          status === ReportStatus.READY &&
          !isLoading &&
          styles.hover,
        className
      )}
      onMouseEnter={() =>
        !disabled &&
        refetchClick &&
        status === ReportStatus.READY &&
        !isLoading &&
        setToggle(true)
      }
      onMouseLeave={() =>
        !disabled &&
        refetchClick &&
        status === ReportStatus.READY &&
        !isLoading &&
        setToggle(false)
      }
    >
      <header className={styles.header}>
        {type === ReportType.EVENT_SURVEY && isNullOrUndefined(reportData) ? (
          <ClockIcon />
        ) : (
          renderIcon
        )}
        <span>{title}</span>
      </header>
      <main className={styles.content}>
        <CSSTransition
          in={
            status !== ReportStatus.GENERATING &&
            status !== ReportStatus.NOT_AVAILABLE
          }
          timeout={500}
          classNames={{
            enter: styles['fade-enter'],
            enterActive: styles['fade-enter-active'],
            exit: styles['fade-exit'],
            exitActive: styles['fade-exit-active'],
          }}
          unmountOnExit
        >
          <div key='fileIcon'>
            {type === ReportType.EVENT_SURVEY &&
            isNullOrUndefined(reportData) ? (
              <Fragment />
            ) : (
              renderFileIcon
            )}
            {renderContent}
          </div>
        </CSSTransition>
        <CSSTransition
          in={[ReportStatus.GENERATING, ReportStatus.NOT_AVAILABLE].includes(
            status!
          )}
          timeout={500}
          classNames={{
            enter: styles['fade-enter'],
            enterActive: styles['fade-enter-active'],
            exit: styles['fade-exit'],
            exitActive: styles['fade-exit-active'],
          }}
          unmountOnExit
        >
          <Fragment key='loader'>
            <div>
              <ThreeDotsLoader />
              <div>
                <span>Gerando seu relatório.</span>
              </div>
            </div>
          </Fragment>
        </CSSTransition>
        {type === ReportType.EVENT_SURVEY && isNullOrUndefined(reportData) ? (
          <Fragment />
        ) : status === ReportStatus.READY ? (
          <IconButton
            onClick={handleButtonClick}
            icon={
              isLoading ? <PercentLoaderIcon indefinido /> : <DownloadIcon />
            }
          />
        ) : !disabled &&
          (status === ReportStatus.ERROR || isNullOrUndefined(status)) ? (
          <IconButton
            onClick={handleButtonClick}
            icon={
              isLoading ? (
                <PercentLoaderIcon indefinido />
              ) : (
                <RefreshIcon animate={false} />
              )
            }
          />
        ) : (
          <Fragment />
        )}
      </main>
      <CSSTransition
        in={
          (status === ReportStatus.READY || isNullOrUndefined(status)) &&
          !isLoading &&
          (toggle || expand)
        }
        timeout={300}
        classNames={{
          enter: styles['content-enter'],
          enterActive: styles['content-enter-active'],
          enterDone: styles['content-enter-done'],
          exit: styles['content-exit'],
          exitActive: styles['content-exit-active'],
        }}
        unmountOnExit
        nodeRef={contentRef}
      >
        <div
          ref={contentRef}
          className={styles['toggle-content']}
          key='toggleContent'
        >
          <button
            className={styles['button-footer']}
            onClick={handleRefetchClick}
          >
            <span>Gerar novo relatório</span>
            <RefreshIcon animate={false} />
          </button>
        </div>
      </CSSTransition>
      {!dateIsAfter && !isNullOrUndefined(nextDate) ? (
        <div>
          <button
            className={clsx(styles['button-footer'], styles.warning)}
            onClick={handleRefetchClick}
          >
            <span>
              Este relatório só pode ser gerado novamente às{' '}
              {moment(nextDate).format(DateFormats.DateFullTime2)}
            </span>
          </button>
        </div>
      ) : (
        <Fragment />
      )}
    </div>
  );
};

export default RelatoryCard;
